import { useState } from "react";
import { Link } from "react-router-dom";
import { linkPaths, retrySyncJob } from "lib/adminSiteApi";
import { hasCapability, useAdmin } from "../RequireAdmin";
import {
  ActionForm,
  Badge,
  ErrorNotice,
  Loading,
  formSchema,
  label,
  requiredText,
  useRemote,
} from "./Controls";
import { date } from "./Records";

// Website Link v1 admin view (docs/TMS_CONNECTION_PLAN_2026-10-06.md section 11, D10, D20).
// Admin labels for outbox statuses; the badge keeps the status class (existing colours).
const LABELS = {
  queued: "Waiting",
  retry: "Retrying",
  running: "Retrying",
  dead_letter: "Failed",
  blocked_configuration: "Blocked",
  accepted: "Sent",
  skipped: "Skipped",
  superseded: "Skipped",
};
export const STATUS_FILTERS = [
  ["waiting", "Waiting"],
  ["retrying", "Retrying"],
  ["failed", "Failed"],
  ["blocked", "Blocked"],
  ["sent", "Sent"],
  ["skipped", "Skipped"],
];
export const GROUP_FILTERS = [
  ["orders", "Orders"],
  ["leads", "Leads"],
  ["referrals", "Referrals"],
];
const METRICS = [
  ["waiting", "Waiting"],
  ["retrying", "Retrying"],
  ["failed", "Failed"],
  ["blocked", "Blocked"],
  ["sentLast24h", "Sent 24h"],
];
const RETRYABLE = ["dead_letter", "blocked_configuration"];
const WAITING = ["queued", "retry", "running"];
const ACCESS = {
  paperwork_download: "paperwork download",
  course_play: "course play",
};
export const syncLabel = (status) =>
  LABELS[status] || label(String(status ?? "not recorded"));
export const attempts = (row) =>
  Number.isSafeInteger(row?.totalAttempts)
    ? row.totalAttempts
    : Number.isSafeInteger(row?.attempts)
      ? row.attempts
      : 0;
export const tries = (row) =>
  `${attempts(row)} ${attempts(row) === 1 ? "try" : "tries"}`;
const lastError = (row) =>
  row.lastError || row.skipReason || row.holdReason || "—";
const count = (value) =>
  value && Number.isSafeInteger(value.count)
    ? `${value.count}${value.capped ? "+" : ""}`
    : "—";
// The website record a row belongs to; referral rows point at their lead or order.
const RECORDS = {
  order: ["orders", "commerce.read"],
  submission: ["submissions", "crm.read"],
  application: ["applications", "recruiting.read"],
  contact: ["contacts", "crm.read"],
  company: ["companies", "crm.read"],
};
export function websiteRecord(row) {
  const id = String(row?.entityId || "");
  const kind =
    row?.entityType === "referral"
      ? row.subject?.type === "order"
        ? "order"
        : row.subject?.kind === "application"
          ? "application"
          : row.subject?.type === "lead"
            ? "submission"
            : null
      : row?.entityType;
  const short = id.length > 8 ? `${id.slice(0, 8)}…` : id;
  const lead = /^(contact|company|resource|webinar|application)\./.exec(
    String(row?.type),
  )?.[1];
  const text =
    kind === "order"
      ? `MAG-${short.toUpperCase()}`
      : `${row?.subject?.kind || lead || kind || row?.entityType || "record"} ${short}`;
  const [domain, capability] = RECORDS[kind] || [];
  return {
    text,
    full: kind === "order" ? `MAG-${id.toUpperCase()}` : id,
    to: domain && id ? `/admin/${domain}/${encodeURIComponent(id)}` : null,
    capability,
  };
}
function describe(row, event = false) {
  const parts = [
    ...(event ? [row.event || row.type] : []),
    tries(row),
    ...(WAITING.includes(row.status) && row.nextAttemptAt
      ? [`next ${date(row.nextAttemptAt)}`]
      : []),
    ...(RETRYABLE.includes(row.status) && row.lastError ? [row.lastError] : []),
  ];
  return `${syncLabel(row.status).toLowerCase()} (${parts.join(", ")})`;
}
export function SyncRecords() {
  const profile = useAdmin();
  const [tick, setTick] = useState(0),
    [filters, setFilters] = useState({ group: "", status: "" }),
    [cursor, setCursor] = useState(null),
    [pages, setPages] = useState([]),
    [retrying, setRetrying] = useState(null);
  const status = useRemote(linkPaths.status(), tick);
  const list = useRemote(
    linkPaths.sync({ ...filters, ...(cursor ? { after: cursor } : {}), limit: 25 }),
    tick,
  );
  const canRetry = hasCapability(profile, "integrations.retry");
  const choose = (key) => (e) => {
    setFilters({ ...filters, [key]: e.target.value });
    setCursor(null);
    setPages([]);
    setRetrying(null);
  };
  const refresh = () => setTick((n) => n + 1);
  const link = status.data?.link || {};
  return (
    <>
      <section className="aw-panel" aria-labelledby="aw-link-status">
        <div className="aw-row">
          <h2 id="aw-link-status">Link status</h2>
          <button className="aw-button" onClick={refresh}>
            Refresh
          </button>
        </div>
        <Loading state={status}>
          {status.data && (
            <>
              <p>
                Link: {status.data.configured ? "ON" : "OFF"} (
                {status.data.mode || "deferred"})
                {link.state === "down"
                  ? ` · down since ${date(link.downSince)}${link.lastErrorCode ? ` (${link.lastErrorCode})` : ""}`
                  : link.state === "up"
                    ? " · connected"
                    : ""}
                {` · last success ${date(link.lastSuccessAt)}`}
                {` · next round ${date(status.data.nextRoundAt)}`}
              </p>
              <div className="aw-metrics">
                {METRICS.map(([key, name]) => (
                  <div className="aw-metric" key={key}>
                    <span>{name}</span>
                    <strong>{count(status.data.counts?.[key])}</strong>
                  </div>
                ))}
              </div>
            </>
          )}
        </Loading>
      </section>
      <section className="aw-panel">
        <div className="aw-row">
          <h2>Records</h2>
        </div>
        <form className="aw-filter" onSubmit={(e) => e.preventDefault()}>
          <label htmlFor="aw-sync-group">
            Records
            <select
              id="aw-sync-group"
              value={filters.group}
              onChange={choose("group")}
            >
              <option value="">All records</option>
              {GROUP_FILTERS.map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="aw-sync-status">
            Status
            <select
              id="aw-sync-status"
              value={filters.status}
              onChange={choose("status")}
            >
              <option value="">All statuses</option>
              {STATUS_FILTERS.map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </form>
        <Loading state={list}>
          {list.data?.items?.length ? (
            <div
              className="aw-table-wrap"
              role="region"
              tabIndex={0}
              aria-label="TMS sync table; scroll horizontally for more columns"
            >
              <table>
                <caption className="aw-sr">TMS sync records</caption>
                <thead>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col">Website record</th>
                    <th scope="col">Status</th>
                    <th scope="col">Tries</th>
                    <th scope="col">Last error</th>
                    <th scope="col">Next try</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((row) => {
                    const record = websiteRecord(row);
                    const event = row.event || row.type;
                    return (
                      <tr key={row.id}>
                        <th scope="row">
                          <Link
                            className="aw-link"
                            to={`/admin/sync/${encodeURIComponent(row.id)}`}
                          >
                            {event}
                          </Link>
                        </th>
                        <td>
                          {record.to &&
                          hasCapability(profile, record.capability) ? (
                            <Link
                              className="aw-link"
                              to={record.to}
                              title={record.full}
                              aria-label={record.full}
                            >
                              {record.text}
                            </Link>
                          ) : (
                            <span title={record.full}>{record.text}</span>
                          )}
                        </td>
                        <td>
                          <Badge
                            value={row.status}
                            text={syncLabel(row.status)}
                          />
                        </td>
                        <td>{attempts(row)}</td>
                        <td>{lastError(row)}</td>
                        <td>
                          {WAITING.includes(row.status)
                            ? date(row.nextAttemptAt)
                            : "—"}
                        </td>
                        <td className="aw-sync-action">
                          {canRetry && RETRYABLE.includes(row.status) ? (
                            <button
                              className="aw-button aw-small"
                              aria-expanded={retrying?.id === row.id}
                              onClick={() => setRetrying(row)}
                            >
                              Retry<span className="aw-sr"> {event}</span>
                            </button>
                          ) : (
                            <Link
                              className="aw-link"
                              to={`/admin/sync/${encodeURIComponent(row.id)}`}
                            >
                              Open<span className="aw-sr"> {event}</span>
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="aw-empty">No records match this view.</p>
          )}
        </Loading>
        <div className="aw-row aw-pagination">
          <button
            className="aw-button"
            disabled={list.loading || !pages.length}
            onClick={() => {
              setCursor(pages[pages.length - 1]);
              setPages(pages.slice(0, -1));
            }}
          >
            Previous
          </button>
          <span>Page {pages.length + 1}</span>
          <button
            className="aw-button"
            disabled={list.loading || !list.data?.next}
            onClick={() => {
              setPages([...pages, cursor]);
              setCursor(list.data.next);
            }}
          >
            Next
          </button>
        </div>
      </section>
      {retrying && (
        <ActionForm
          key={retrying.id}
          title={`Retry ${retrying.event || retrying.type}`}
          description={`Record ${websiteRecord(retrying).full}: ${syncLabel(retrying.status).toLowerCase()} after ${tries(retrying)} (${lastError(retrying)}). Retry only after resolving the recorded failure. This returns the job to its queue with a fresh 24-hour retry window; a queued retry is not a successful synchronization.`}
          schema={formSchema({ reason: requiredText() }, ["reason"])}
          button="Queue retry"
          submit={(body, key) => retrySyncJob(retrying.id, body, key)}
          onDone={refresh}
        />
      )}
      <p className="aw-notice">
        Website records remain available while TMS is disconnected. Open a job
        to inspect its payload, receipt and retry options.
      </p>
    </>
  );
}
// One "TMS sync" line for a website record (order, enquiry or application detail).
export function SyncLine({ type, id }) {
  const state = useRemote(linkPaths.entity(type, id));
  if (state.loading)
    return (
      <p className="aw-muted" role="status">
        TMS sync: loading…
      </p>
    );
  if (state.error) return <ErrorNotice error={state.error} />;
  const rows = (state.data?.items || []).filter(
    (r) =>
      r && typeof r.id === "string" && typeof r.type === "string" && r.status,
  );
  const primary =
    type === "order"
      ? rows.find((r) => r.type === "order.paid") || rows[0]
      : rows.find((r) => r.group === "lead") || rows[0];
  const reversal =
    type === "order"
      ? rows.find((r) => ["order.refunded", "order.chargeback"].includes(r.type))
      : null;
  if (!primary) return <p>TMS sync: not queued</p>;
  const view = (row, name) => (
    <Link className="aw-link" to={`/admin/sync/${encodeURIComponent(row.id)}`}>
      View<span className="aw-sr"> {name} TMS sync record</span>
    </Link>
  );
  return (
    <p>
      TMS sync: {describe(primary, type !== "order")} {view(primary, primary.event || primary.type)}
      {reversal && (
        <>
          {" · "}
          {reversal.type === "order.chargeback" ? "Chargeback" : "Refund"}:{" "}
          {describe(reversal)} {view(reversal, reversal.type)}
        </>
      )}
    </p>
  );
}
// The order "Refundable" line (D10): website evidence only until the TMS read-back exists.
export function RefundableLine({ orderId }) {
  const state = useRemote(linkPaths.refundable(orderId));
  if (state.loading)
    return (
      <p className="aw-muted" role="status">
        Refundable: checking…
      </p>
    );
  if (state.error) return <ErrorNotice error={state.error} />;
  const r = state.data;
  if (typeof r?.refundable !== "boolean") return null;
  return (
    <p>
      Refundable:{" "}
      {r.refundable
        ? "yes (no download or play yet)"
        : `no (first access ${date(r.websiteFirstAccessAt)}, ${ACCESS[r.source] || "website access"})`}
      {r.tms === "not_connected" ? " · TMS: not connected" : ""}
    </p>
  );
}
